import * as Braze from '@braze/web-sdk';
import { toast } from 'react-hot-toast';

import { getTaxList } from './api_tax';

import { startBrazeSDK, stopBrazeSDK } from '../services/service_braze';

import { removeData } from '../utils/util_helpers';
import { removeUniqueID, retrieveUniqueID, saveToken } from '../utils/util_authentication';
import { removeToken } from '../utils/util_authentication';
import { handleFirstResponse } from '../utils/util_api_response';
import { checkErrorExist, convertError } from './converters';
import { apiWrapper } from './helpers';
import { getGlobalState, setGlobalState, STATE_KEYS } from '../configs/config_state';

// Global Variables Import
import GLOBAL from '../configs/config_global';
import routeKeys from '../navigation/RouteKeys';
import { checkAccountExist } from '../utils/util_main_app';

// region Function - Set Tracking User
export function setTrackingUser(vendorData) {
    // Start Services
    startBrazeSDK();

    // Change User Data for tracking
    if (!vendorData) return;

    // Set user info for tracking data
    const userID = vendorData.id ? (vendorData?.id?.toString() + (getGlobalState(STATE_KEYS.IS_DEV) ? '-Dev' : '')) : null;
    const userName = vendorData.name || '(No Name)';
    const userEmail = (vendorData.email.endsWith('-dev') ? vendorData.email.replace('-dev', '') : vendorData.email) || '(No Email)';
    const userPhone = vendorData.phone;

    // Braze
    if (GLOBAL.BRAZED_INITIATED) {
        Braze.changeUser(userID);
        Braze.openSession();
        setTimeout(() => {
            Braze.getUser()?.setEmail(userEmail);
            Braze.getUser()?.setFirstName(userName);
            Braze.getUser()?.setPhoneNumber(userPhone);
        }, 500);
    }
}
// endregion

// region Function - Clear User Data
export function clearAllUserData() {
    // Clear global data
    GLOBAL.DATA.LOGIN_SERVICE = null;
    GLOBAL.DATA.USER_TOKEN = null;
    GLOBAL.DATA.USER = {};

    // Save to global state
    setGlobalState(STATE_KEYS.TRUCK_DATA, {});
    setGlobalState(STATE_KEYS.LOCATION_LIST, []);
    setGlobalState(STATE_KEYS.LOGGED_IN, false);

    // Remove token from local storage
    removeData('currentToken');
    removeData('accountListIndex');

    // Clear saved location
    removeData('currentLocation');

    // Remove ALL unique IDs
    removeUniqueID(null);

    // Stop Braze SDK
    stopBrazeSDK();
}
// endregion

// region Function - Update Global Data
export function updateGlobalData(token, vendorData, loginType = 'square', skipTaxFetch = false, newWaitTimesData = {}) {
    return new Promise((resolve) => {
        const confirmFunc = (taxObj = null) => {
            const isSameVendor = (GLOBAL.DATA.USER?.id === vendorData.id);

            // Set tracking data
            if (!Object.keys(newWaitTimesData).length) setTrackingUser(vendorData);

            // Generate truck data
            const truckData = Object.assign({}, vendorData['trucks'][0], { phone: vendorData.phone, email: vendorData.email || 'vendor.support@gotruckster.com' }, newWaitTimesData);

            // Save vendor data to global state
            GLOBAL.DATA.USER_TOKEN = token;
            GLOBAL.DATA.USER = isSameVendor ? Object.assign({}, GLOBAL.DATA.USER, vendorData) : vendorData;
            GLOBAL.DATA.LOGIN_SERVICE = loginType;
            if (taxObj) {
                GLOBAL.DATA.SALES_TAX_OPTIONS = isSameVendor ? Object.assign({}, GLOBAL.DATA.SALES_TAX_OPTIONS, taxObj) : taxObj;
            }

            // Set to main state data
            setGlobalState(STATE_KEYS.TRUCK_DATA, truckData);
            setGlobalState(STATE_KEYS.LOCATION_LIST, vendorData.locations || []);
            setGlobalState(STATE_KEYS.CURRENT_TAB_STACK, routeKeys.PROFILE_STACK);
            setGlobalState(STATE_KEYS.LOGGED_IN, true);

            // Save user token to local storage
            saveToken(token, loginType, vendorData)
                .then(() => resolve(vendorData))
                .catch((errorStorage) => resolve(errorStorage));
        };

        if (skipTaxFetch) {
            confirmFunc(null);
        }
        else {
            // Get tax option list
            getTaxList(token)
                .then((taxList) => {
                    const taxObj = {};
                    taxList.forEach((item) => taxObj[item.id] = item);
                    confirmFunc(taxObj);
                })
                .catch((error) => {
                    toast('Unable to fetch your sales tax options, please try again later in the Account Settings screen.<br/><br/>Error Message: ' + error);
                    confirmFunc(null);
                });
        }
    });
}
// endregion

// region POST - Login with Email
export function loginWithEmail(email, password, ignoreIfExisted = false) {
    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + `/vendor/v1/account/signin`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                email,
                password,
                provider: 'email'
            }),
            timeout: 10000
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.hasOwnProperty('errors')) {
                    console.log('[SUCCESS] LOGIN WITH EMAIL', response);

                    // Retrieve user data from server
                    getUserData(response.token, 'email', ignoreIfExisted)
                        .then((userDataResponse) => resolve(userDataResponse))
                        .catch((errorUserData) => reject(errorUserData));
                }
                else {
                    console.log('[FAILED] LOGIN WITH EMAIL', response);
                    reject(convertError(response.errors));
                }
            })
            .catch((error) => {
                console.log('[FAILED] LOGIN WITH EMAIL');
                reject(error.message);
            });
    });
}
// endregion

// region POST - Register with Email
export function registerNormal(name, email, password, phone, phoneOptIn, truckID) {
    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + `/vendor/v1/account/signup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                name,
                email,
                password,
                'phone': phone.toString(),
                'phone_opt_in': phoneOptIn,
                'password_confirmation': password,
                'truck_id': truckID
            }),
            timeout: 10000
        })
            .then((response) => response.json())
            .then((response) => {
                if (!checkErrorExist(response)) {
                    console.log('[SUCCESS] CREATE VENDOR ACCOUNT', response);
                    resolve(response);
                }
                else {
                    console.log('[FAILED] CREATE VENDOR ACCOUNT', response);
                    reject(convertError(response.errors));
                }
            })
            .catch((error) => {
                console.log('[FAILED] CREATE VENDOR ACCOUNT');
                reject(convertError(error));
            });
    });
}
// endregion

// region POST - Register Custom Truck Profile
export function registerCustom(dataObj = {}) {
    const data = new FormData();
    Object.keys(dataObj).forEach((key) => (key !== 'licenseNumberArr') && data.append(key, dataObj[key]));
    const licenseArr = dataObj['licenseNumberArr'] || [];
    licenseArr.forEach((item, index) => {
        data.append(`license_numbers[${index}][city_id]`, item.id);
        data.append(`license_numbers[${index}][license_number]`, item.value);
    });

    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + `/vendor/v1/account/create-food-truck-profile`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json'
            },
            body: data,
            timeout: 10000
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.hasOwnProperty('errors')) {
                    resolve(response);
                    console.log('[SUCCESS] CREATE CUSTOM TRUCK PROFILE', response);
                }
                else {
                    reject(convertError(response.errors));
                    console.log('[FAILED] CREATE CUSTOM TRUCK PROFILE', response);
                }
            })
            .catch((error) => {
                console.log('[FAILED] CREATE CUSTOM TRUCK PROFILE');
                reject(convertError(error));
            });
    });
}
// endregion

// region POST - Authorize With Square
export function authorizeWithSquare(code, truckID) {
    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + `/vendor/v1/account/squareup-auth/${truckID}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + GLOBAL.DATA.USER_TOKEN
            },
            body: JSON.stringify({ code })
        })
            .then((response) => {
                console.log(response.status);
                return response.json();
            })
            .then((response) => {
                if (!checkErrorExist(response)) {
                    console.log('[SUCCESS] SQUARE AUTHORIZE LINK', response);
                    resolve(response);
                }
                else {
                    console.log('[FAILED] SQUARE AUTHORIZE LINK', response);

                    let errorMessage = convertError(response.errors);

                    // Show error to user
                    if (errorMessage.includes('Your account is not exist, please register first')) {
                        reject(errorMessage, response.squareup_token);
                    }
                    else reject(errorMessage);
                }
            })
            .catch((loginError) => {
                console.log('[FAILED] SQUARE AUTHORIZE LINK', loginError);
                reject(loginError);
            });
    });
}
// endregion

// region GET - User Data
export function getUserData(userToken, service = 'email', ignoreIfExisted = false) {
    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + '/vendor/v1/account/detail', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + userToken
            },
            timeout: 10000
        })
            .then((response) => handleFirstResponse(response))
            .then((response) => {
                if (!checkErrorExist(response)) {
                    console.log('[SUCCESS] GET USER DATA', response);

                    if (response['trucks']?.length > 0) {
                        if (ignoreIfExisted && checkAccountExist(response.email, service)) {
                            reject('Account existed');
                        }
                        else {
                            const truckData = response['trucks'][0];

                            // Parse Wait Times Data
                            let waitTimesData = {
                                wait_time_status: truckData['wait_time_status'],
                                wait_time_type: truckData['wait_time_type'],
                                estimated_wait_times: truckData['estimated_wait_times']
                            };

                            // Update global data
                            updateGlobalData(userToken, Object.assign(response, { waitTimesData }), service)
                                .then(() => resolve(response));
                        }
                    }
                    else reject('Your user profile was found, but no truck profile is associated with your account');
                }
                else {
                    console.log('[FAILED] GET USER DATA', response);
                    reject('No response user data');
                }
            })
            .catch((error) => {
                console.log('[FAILED] GET USER DATA', error);
                reject(error.message);
            });
    });
}
// endregion

// region GET - List of Square Locations
export function getSquareLocations(accessToken) {
    return new Promise((resolve, reject) => {
        fetch('https://connect.squareup.com/v2/locations', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + accessToken
            },
            timeout: 10000
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.message) {
                    console.log('[SUCCESS] Get Square Location', response);
                    let locations = response.locations;
                    let qualifiedLocations = [];
                    locations.forEach((loc) => {
                        if (loc.capabilities && loc.capabilities.indexOf('CREDIT_CARD_PROCESSING') !== -1) {
                            qualifiedLocations.push(loc);
                        }
                    });
                    resolve(qualifiedLocations);
                }
                else {
                    console.log('[FAILED] Get Square Location', response);
                    reject(response.message);
                }
            })
            .catch((error) => {
                console.log('[FAILED] Get Square Location', error);
                reject(error.message);
            });
    });
}
// endregion

// region PATCH - Update User Data
export function updateUserData(name = null, phone = null) {
    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + '/vendor/v1/account/update', {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + GLOBAL.DATA.USER_TOKEN
            },
            body: JSON.stringify({ name, phone }),
            timeout: 10000
        })
            .then((response) => response.json())
            .then((response) => {
                if (!checkErrorExist(response)) {
                    console.log('[SUCCESS] UPDATE USER DATA', response);
                    updateGlobalData(GLOBAL.DATA.USER_TOKEN, Object.assign({}, response, { trucks: [getGlobalState(STATE_KEYS.TRUCK_DATA)] }), response['has_password'] ? 'email' : 'square', true);
                    resolve('Done');
                }
                else {
                    console.log('[FAILED] UPDATE USER DATA', response);
                    reject(convertError(response.errors));
                }
            })
            .catch((error) => {
                console.log('[FAILED] UPDATE USER DATA', error);
                reject(error.message);
            });
    });
}
// endregion

// region POST - Update User Data
export function changePassword(password, newPassword = '') {
    return new Promise((resolve, reject) => {
        if (!GLOBAL.DATA.USER['has_password']) {
            reject('Cannot change password for your Square account');
            return;
        }

        fetch(GLOBAL.FATHER_LINK + '/vendor/v1/account/change-password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + GLOBAL.DATA.USER_TOKEN
            },
            body: JSON.stringify({
                'current_password': password,
                'password_confirmation': newPassword,
                'password': newPassword
            }),
            timeout: 10000
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.hasOwnProperty('errors')) {
                    console.log('[SUCCESS] CHANGE PASSWORD', response);
                    updateGlobalData(response.token, GLOBAL.DATA.USER, GLOBAL.DATA.USER['has_password'] ? 'email' : 'square', false);
                    resolve('Done');
                }
                else {
                    console.log('[FAILED] CHANGE PASSWORD', response);
                    reject(convertError(response.errors));
                }
            })
            .catch((error) => {
                console.log('[FAILED] CHANGE PASSWORD', error);
                reject(error.message);
            });
    });
}
// endregion

// region POST - Send Reset Password Email
export function sendResetPasswordEmail(email) {
    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + '/vendor/v1/account/forgot-password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({ email }),
            timeout: 10000
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.hasOwnProperty('errors')) {
                    console.log('[SUCCESS] SEND RESET PASSWORD EMAIL', response);
                    resolve('Done');
                }
                else {
                    console.log('[FAILED] SEND RESET PASSWORD EMAIL', response);
                    reject(convertError(response.errors));
                }
            })
            .catch((error) => {
                console.log('[FAILED] SEND RESET PASSWORD EMAIL', error);
                reject(error.message);
            });
    });
}
// endregion

// region POST - Check Vendor Email Exists
export function checkVendorEmailExist(email) {
    return new Promise((resolve, reject) => {
        fetch(GLOBAL.FATHER_LINK + '/vendor/v1/account/vendor_email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({ email }),
            timeout: 10000
        })
            .then((response) => {
                switch(response.status) {
                    case 200:
                    {
                        resolve('Done');
                        break;
                    }
                    case 422:
                    {
                        reject('Already Existed');
                        break;
                    }
                    default: throw (response);
                }
            })
            .catch((error) => {
                console.log('[FAILED] CHECK EXISTING EMAIL', error);
                reject(error.message);
            });
    });
}
// endregion

// region POST - Log Out
export function logOut(accountID = GLOBAL.DATA.USER?.id, accountToken = GLOBAL.DATA.USER_TOKEN) {
    return new Promise((resolve, reject) => {
        const mainFunc = () => {
            removeToken(accountID)
                .then((newAccountList) => {
                    console.log('[SUCCESS] NORMAL LOGOUT');
                    removeUniqueID(accountID);
                    resolve(newAccountList || []);
                });
        };

        // Retrieve the unique ID of this account on this browser
        retrieveUniqueID(accountID)
            .then((uniqueID) => {
                console.log('[SUCCESS] RETRIEVE UNIQUE ID', uniqueID);
                if (uniqueID) {
                    apiWrapper('/vendor/v1/account/logout', 'POST', 'LOG OUT API', JSON.stringify({ device_id: uniqueID }))
                        .then(() => mainFunc())
                        .catch((error) => reject(error));
                }
                else mainFunc();
            });
    });
}
// endregion

// region DELETE - Account
export function deleteAccount() {
    return apiWrapper(
        `/vendor/v1/account/${GLOBAL.DATA.USER.id}/destroy`,
        'DELETE',
        'DELETE ACCOUNT'
    );
}
// endregion
